/**
 * Linhas do DRE (demonstração do resultado) e o plano de contas padrão.
 *
 * Regra de ouro: cada categoria financeira aponta para UMA linha do DRE (`dreGroup`).
 * `null` significa "fora do resultado": dinheiro que entra ou sai do caixa mas não é
 * receita nem despesa (compra de mercadoria para revenda, aporte do sócio, empréstimo).
 * Compra de mercadoria vira custo só quando a peça é vendida, pelo custo do item (CMV).
 */

export const DRE_GROUPS = [
  'gross_revenue',
  'deductions',
  'cogs',
  'variable_expenses',
  'personnel',
  'occupancy',
  'administrative',
  'marketing',
  'financial',
  'investments',
  'other',
] as const;
export type DreGroupKey = (typeof DRE_GROUPS)[number];

export const DRE_GROUP_LABELS: Record<DreGroupKey, string> = {
  gross_revenue: 'Receita de vendas e serviços',
  deductions: 'Impostos sobre vendas',
  cogs: 'Custo das mercadorias vendidas',
  variable_expenses: 'Despesas variáveis',
  personnel: 'Pessoal',
  occupancy: 'Ocupação (aluguel e contas)',
  administrative: 'Administrativas',
  marketing: 'Marketing',
  financial: 'Financeiro (juros e tarifas)',
  investments: 'Investimentos (fora do resultado)',
  other: 'Outras receitas e despesas',
};

/** Rótulo de `dreGroup = null`. */
export const DRE_OUTSIDE_LABEL = 'Fora do resultado (compras para estoque, aportes e retiradas)';

export function dreGroupLabel(group: DreGroupKey | null | undefined): string {
  return group ? DRE_GROUP_LABELS[group] : DRE_OUTSIDE_LABEL;
}

/**
 * Linhas que cada tipo de categoria pode usar. `cogs` não é escolhível: o CMV é calculado
 * pelo custo das peças vendidas, e uma categoria ali contaria o mesmo custo duas vezes.
 */
export const DRE_GROUPS_BY_TYPE: { income: readonly DreGroupKey[]; expense: readonly DreGroupKey[] } = {
  income: ['gross_revenue', 'financial', 'other', 'investments'],
  expense: ['deductions', 'variable_expenses', 'personnel', 'occupancy', 'administrative', 'marketing', 'financial', 'other', 'investments'],
};

export function isDreGroupAllowed(type: 'income' | 'expense', group: DreGroupKey | null): boolean {
  return group === null || DRE_GROUPS_BY_TYPE[type].includes(group);
}

/** Despesas fixas (estrutura da loja): entram no ponto de equilíbrio. */
export const FIXED_EXPENSE_GROUPS: readonly DreGroupKey[] = ['personnel', 'occupancy', 'administrative', 'marketing'];

/**
 * Linha do DRE de cada categoria padrão (DEFAULT_FINANCE_CATEGORIES).
 * - "Outras receitas" vai para `other` (abaixo do resultado operacional): é o balde de
 *   reembolsos, venda de um móvel usado, prêmios. `financial` fica reservado para juros,
 *   rendimentos e tarifas, para que a linha "Resultado financeiro" diga só isso.
 * - "Fornecedores" fica FORA do resultado: comprar mercadoria para revender é estoque.
 *   O custo entra no DRE quando a peça é vendida (CMV).
 */
export const DEFAULT_CATEGORY_DRE_GROUPS: { income: Record<string, DreGroupKey | null>; expense: Record<string, DreGroupKey | null> } = {
  income: {
    'Vendas avulsas': 'gross_revenue',
    Serviços: 'gross_revenue',
    'Outras receitas': 'other',
  },
  expense: {
    Fornecedores: null,
    Aluguel: 'occupancy',
    Salários: 'personnel',
    Impostos: 'deductions',
    Marketing: 'marketing',
    'Taxas e tarifas': 'financial',
    'Contas (água, luz, internet)': 'occupancy',
    'Outras despesas': 'administrative',
  },
};

/** Linha padrão de uma categoria nova quando a pessoa não escolhe. */
export function defaultDreGroupForNewCategory(type: 'income' | 'expense'): DreGroupKey {
  return type === 'income' ? 'other' : 'administrative';
}
